/**
 * author: Gautam Panickar
 */
import moment from "moment";
import Multiselect from "multiselect-react-dropdown";
import React, { useContext, useEffect, useState } from "react";
import { Badge, Button, Form, Modal } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import DatePicker from "react-datepicker";
import { withTranslation } from "react-i18next";
import { BiUser } from "react-icons/bi";
import { toast } from "react-toastify";
import { CompanyContext, RegulationContext } from "../../../App";
import { TaskStatus, TaskPriority } from "../../../core/constants/common";
import CompanyDashboardService from "../../../core/service/CompanyDashboardService";
import SupplierService from "../../../core/service/SupplierService";
import TaskService from "../../../core/service/TaskService";
import Utils from "../../../core/utilities/Utils";

const TaskModal = ({ t, id, showModal, isActionBased, modalData, onSaveCallBack, onCloseCallback }) => {
	const authorityData = JSON.parse(localStorage.getItem("authority"));
	const [show, setShow] = useState(showModal ? true : false);
	const assignees = [];
	modalData?.assignedUsers?.forEach(u => assignees.push(u.id));
	const [formData, setFormData] = useState({
		title: modalData?.title ? modalData.title : "",
		description: modalData?.description ? modalData.description : "",
		status: modalData?.status ? TaskStatus[`${modalData.status}`] : TaskStatus.NEW,
		priority: modalData?.priority ? TaskPriority[`${modalData.priority}`] : TaskPriority.LOW,
		dueDate: modalData?.dueDate ? new Date(modalData.dueDate) : new Date(),
		startDate: modalData?.startDate ?? null,
		assignTo: 'COMPANY',
		assignees: assignees,
		alertId: modalData?.alertId ?? 0
	});
	const [reporters, setReporters] = useState([]);
	const [companyUsers, setCompanyUsers] = useState([]);
	const [validated, setValidated] = useState(false);
	const [openedFor, setOpenedFor] = useState(modalData?.openedFor ?? "NEW");

	const { glbRegulation } = useContext(RegulationContext);
	const { glbCompany } = useContext(CompanyContext);

	const glbRegulationId = glbRegulation?.id ?? "";
	const glbCompanyId = glbCompany?.companyId ?? "";
	const loggedInUserCompanyId = authorityData.profile.company.id;

	useEffect(() => {
		if (authorityData.roles.includes("SUPPLIER_ADMIN")) {
			getCompanyUsers();
		} else {
			getCompanyUsers();
			getSuppliers();
		}
	}, [id, modalData]);

	/**
	 * For a supplier admin fetch the company connections
	 */
	const getConnections = () => {
		SupplierService.companyConnections(authorityData.profile.company.id)
			.then((response) => {
				setReporters([...(response.data?.filter(r => r.invitationStatus === "ONBOARDED") ?? [])]);
			})
			.catch((error) => {
			});
	};

	/**
	 * For company or super admin, get all suppliers
	 */
	const getSuppliers = () => {
		SupplierService.listAllSuppliers(authorityData.profile.company.id)
			.then((response) => {
				setReporters([...(response.data?.filter(r => r.invitationStatus === "ONBOARDED") ?? [])]);
			})
			.catch((error) => { });
	};

	/**
	 * Gets the users for the company
	 */
	const getCompanyUsers = () => {
		CompanyDashboardService.getUsers("")
			.then((response) => {
				setCompanyUsers([...response.data]);
			}).catch((error) => {
			});
	}

	const handleClose = () => {
		setShow(false);
		onCloseCallback();
	};

	const handleSubmit = () => {
		if (!formData.title || formData.title?.length > 1000
			|| !formData.description || formData.description?.length > 4000
			|| formData.priority < 0 || (
				(formData.assignTo === "SUPPLIER"
					|| authorityData.roles.includes("SUPPLIER_ADMIN"))
				&& formData.assignees.length <= 0)) {
			setShow(true);
		} else {
			let payload = {
				...formData
			};

			const assignees = [];
			formData.assignees.forEach(a => { assignees.push({ id: a }) });
			if (id === 0) {
				payload.assignedCompanies = formData.assignTo === "SUPPLIER" ? assignees : [];
				payload.assignedUsers = formData.assignTo === "SUPPLIER" ? [] : assignees;
			} else {
				payload.assignedUsers = canEditAssignees ? assignees : [];
			}
			delete payload.assignees;
			delete payload.assignTo;
			delete payload.alertId;
			if (isActionBased) {
				payload = {
					...payload,
					sourceCompany: { id: authorityData.profile.company.id },
					status: TaskStatus.NEW
				};
				CompanyDashboardService.convertAlerttoTask(payload, formData.alertId)
					.then((response) => {
						if (response.status === 200) {
							toast.success("Success", {
								position: toast.POSITION.TOP_RIGHT,
							});
							setShow(false);
							onSaveCallBack();
						}
					}).catch((error) => {
					});

			} else {
				payload = {
					...payload,
					sourceCompany: { id: glbCompanyId || 0 },
					regulation: { id: glbRegulationId }
				};
				if (id > 0) {
					payload.id = id;
				} else {
					payload.status = TaskStatus.NEW;
				}
				const apiCall = id > 0
					? TaskService.updateTask(id, payload)
					: TaskService.createTask(payload);
				apiCall.then((response) => {
					if (response.status === 201 || response.status === 200) {
						toast.success("Success", {
							position: toast.POSITION.TOP_RIGHT,
						});
						setShow(false);
						onSaveCallBack();
					}
				}).catch((error) => {
				});
			}
		}
		setValidated(true);
	};

	const handleEditClick = () => {
		setOpenedFor("EDIT");
	};

	const getStatusText = (status) => {
		switch (status) {
			case TaskStatus.CLOSED:
				return "Closed";
			case TaskStatus.IN_PROGRESS:
				return "In Progress";
			default:
				return "New";
		}
	}

	const getSelectedAssignees = (assignees) => {
		if (formData.assignTo === "SUPPLIER") {
			return reporters.filter(r => assignees.indexOf(r.id) >= 0);
		} else {
			return companyUsers.filter(u => assignees.indexOf(u.id) >= 0);
		}
	};

	const onSelectDeSelectAssignee = (selectedItem) => {
		const assignees = [...formData.assignees];
		const index = formData.assignees.indexOf(selectedItem.id);
		if (index >= 0) {
			assignees.splice(index, 1);
		} else {
			if (formData.assignTo === "SUPPLIER") {
				assignees.push(selectedItem.companyId);
			} else {
				assignees.push(selectedItem.id);
			}
		}
		setFormData({
			...formData,
			assignees: assignees
		});
	};

	const canEditAssignees = () => {
		if (id > 0) {
			return modalData.defaultAssignee?.id > 0
				&& modalData.sourceCompany?.id === authorityData.profile?.company?.id
				? false : true;
		} else {
			return true;
		}
	};

	const getAssignedUsersToRender = () => {
		if (loggedInUserCompanyId !== modalData.ownerCompany?.id && modalData.defaultAssignee?.id > 0) {
			return <><BiUser /> <span>{modalData.defaultAssignee?.name}</span></>;
		} else {
			return modalData.assignedUsers?.map(user => {
				return <><BiUser /> <span>{user.name}</span></>
			});
		}

	};

	return (
		<>
			<Modal
				show={show}
				onHide={handleClose}
				size="md"
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">
						{
							isActionBased ? t("TakeanAction.label")
								:
								id > 0
									? openedFor === "VIEW" ? t("ViewTask.label") : t("EditTask.label")
									: t("NewTask.label")
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{
						openedFor === "VIEW"
							?
							<div className="ViewTask_card">
								<div className="ViewTask_card_head">

									<h6>
										{formData.title}
									</h6>
									<ul>
										<li>
											<Badge bg={formData.status === TaskStatus.CLOSED
												? "badge-Closed" : formData.status === TaskStatus.IN_PROGRESS
													? "badge-Inprogress" : "badge-New"}>
												{getStatusText(formData.status)}
											</Badge>
										</li>
										<li> {getAssignedUsersToRender()}</li>
										<li> {t("Priority.label")} : {Utils.getTaskPriorityText(formData.priority)}</li>
										<li>
											{formData.status === TaskStatus.CLOSED ? "Completed on " : "Due on "}
											{moment(formData.status === TaskStatus.CLOSED
												? modalData.completedDate : formData.dueDate).format("MMM DD, YYYY")}
										</li>
									</ul>
								</div>


								<p className="m-0">
									{formData.description}
								</p>

							</div>
							:
							<Form noValidate validated={validated}>
								<div>
									<div className="py-1">
										<label className="mb-1">{t("Title.label")}</label>
										<input type="text"
											className="form-control"
											required
											maxLength={1000}
											onChange={(e) => setFormData({ ...formData, title: e.target.value })}
											value={formData.title} />
									</div>
									<div className="py-1">
										<label className="mb-1">{t("Description.label")}</label>
										<textarea
											className="form-control"
											required
											maxLength={4000}
											onChange={(e) => setFormData({ ...formData, description: e.target.value })}
											defaultValue={formData.description}>
										</textarea>
									</div>
									{
										id > 0
											?
											<div className="py-1">
												<label className="mb-1">{t("Status.label")}</label>
												<select
													className="form-select"
													onChange={(e) => setFormData({ ...formData, status: e.target.value })}
													defaultValue={formData.status}>
													<option value="" disabled hidden>{t("Select.label")}</option>
													<option value={TaskStatus.NEW} key={TaskStatus.NEW}>
														{getStatusText(TaskStatus.NEW)}
													</option>
													<option value={TaskStatus.IN_PROGRESS} key={TaskStatus.IN_PROGRESS}>
														{getStatusText(TaskStatus.IN_PROGRESS)}
													</option>
													<option value={TaskStatus.CLOSED} key={TaskStatus.CLOSED}>
														{getStatusText(TaskStatus.CLOSED)}
													</option>
												</select>
											</div>
											: <></>
									}
									{
										isActionBased ? <></>
											:
											<div className="py-1">
												<label className="mb-1">{t("Priority.label")}</label>
												<select
													className="form-select"
													onChange={(e) => { setFormData({ ...formData, priority: e.target.value }) }}
													defaultValue={formData.priority}>
													<option value="" disabled hidden>{t("Select.label")}</option>
													<option value={TaskPriority.LOW} key={TaskPriority.LOW}>
														{Utils.getTaskPriorityText(TaskPriority.LOW)}
													</option>
													<option value={TaskPriority.MEDIUM} key={TaskPriority.MEDIUM}>
														{Utils.getTaskPriorityText(TaskPriority.MEDIUM)}
													</option>
													<option value={TaskPriority.HIGH} key={TaskPriority.HIGH}>
														{Utils.getTaskPriorityText(TaskPriority.HIGH)}
													</option>
													<option value={TaskPriority.CRITICAL} key={TaskPriority.CRITICAL}>
														{Utils.getTaskPriorityText(TaskPriority.CRITICAL)}
													</option>
												</select>
											</div>
									}
									<div className="py-1">
										<label className="mb-1">{t("Assignto.label")}</label>
										{
											id === 0 && !authorityData.roles.includes("SUPPLIER_ADMIN") && (
												<div>
													<Form.Check
														inline
														label={t("MyCompany.label")}
														name="reportTo"
														type="radio"
														id="inline-1"
														required
														value="COMPANY"
														checked={formData.assignTo === 'COMPANY'}
														onChange={(e) => setFormData({ ...formData, assignTo: e.target.value })}
													/>
													<Form.Check
														inline
														label={t("Supplier.label")}
														name="reportTo"
														type="radio"
														id="inline-2"
														required
														value="SUPPLIER"
														checked={formData.assignTo === 'SUPPLIER'}
														onChange={(e) => setFormData({ ...formData, assignTo: e.target.value })}
													/>
												</div>)
										}
									</div>
									<div className="py-1">
										<div className="col-lg-12">
											{
												canEditAssignees()
													?
													<Multiselect
														options={id === 0 && formData.assignTo === "SUPPLIER" ? reporters : companyUsers}
														selectedValues={getSelectedAssignees(formData.assignees)}
														onSelect={(selectedList, selectedItem) => {
															onSelectDeSelectAssignee(selectedItem);
														}}
														onRemove={(selectedList, selectedItem) => {
															onSelectDeSelectAssignee(selectedItem);
														}}
														displayValue={formData.assignTo === "SUPPLIER" ? 'legalName' : 'name'}
														placeholder=""
														required />
													:
													getAssignedUsersToRender()
											}
										</div>
									</div>
									<div className="py-1">
										<label className="mb-1">{t("DueDate.label")}</label>
										<DatePicker
											selected={formData.dueDate}
											onChange={(date) => setFormData({ ...formData, dueDate: date })}
											className="form-control"
											required
											dateFormat="dd/MM/yyyy"
										/>
									</div>
								</div>
							</Form>
					}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handleClose}>
						{t("Cancel.label")}
					</Button>
					{

						formData.status === TaskStatus.CLOSED
							? <></>
							: id > 0 && openedFor === "VIEW"
								?
								<Button variant="primary" onClick={() => handleEditClick()} className="px-3">
									{t("Edit.label")}
								</Button>
								:
								<Button variant="primary" onClick={() => handleSubmit()} >
									{id > 0 && openedFor === "EDIT" ? t("Update.label") : t("Create.label")}
								</Button>
					}
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default withTranslation()(TaskModal);
